// src/pages/Login.js
// ──────────────────────────────────────────────────────────────────────────
// Login for students & staff. Accepts a Student ID OR an email (students are
// usually given an ID; staff/admins use email). After login, users flagged
// "must_change_password" are sent to /set-password before anything else.
//
// Self-signup is OFF by default now that accounts are provisioned by the
// institute (admin creates students; Supabase invites for staff). Flip
// ALLOW_SIGNUP to true only if you want open registration (e.g. library).
// ──────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import styled from 'styled-components';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { loginIdentifierToEmail } from '../lib/studentId';
import theme from '../theme/theme';
import logo from '../assets/star.png';

const ALLOW_SIGNUP = false;

const Wrap = styled.section`
  max-width: 460px; margin: 0 auto; padding: 4.5rem ${theme.layout.gutter} 6rem; text-align: center;
  img { height: 72px; width: 72px; object-fit: cover; border-radius: 50%; border: 1px solid ${theme.color.line}; margin: 0 auto 1.2rem; display: block; }
  h1 { font-weight: 400; font-size: 2rem; }
  .sub { color: ${theme.color.inkSoft}; margin: 0.5rem 0 1.8rem; }
  label { display: block; text-align: left; font-size: 0.78rem; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; color: ${theme.color.inkSoft}; margin: 0.9rem 0 0.4rem; }
  input { width: 100%; font-family: ${theme.font.body}; font-size: 1rem; padding: 12px 15px;
          border: 1px solid ${theme.color.line}; border-radius: ${theme.layout.radius}; background: #fff; color: ${theme.color.ink}; }
  input:focus { outline: none; border-color: ${theme.color.accent}; }
  .hint { text-align: left; font-size: 0.78rem; color: ${theme.color.inkSoft}; margin-top: 0.4rem; }
  button.primary { margin-top: 1.4rem; width: 100%; background: ${theme.color.accent}; color: ${theme.color.onAccent};
                   border: none; font-family: ${theme.font.body}; font-weight: 700; font-size: 0.95rem; padding: 13px; border-radius: ${theme.layout.radius}; cursor: pointer; }
  button.primary:hover { background: ${theme.color.accentDark}; } button.primary:disabled { opacity: 0.6; cursor: default; }
  .toggle { margin-top: 1.4rem; font-size: 0.9rem; color: ${theme.color.inkSoft}; }
  .toggle button { background: none; border: none; color: ${theme.color.accent}; font-weight: 700; cursor: pointer; font-family: inherit; font-size: 0.9rem; }
  .err { color: #b3261e; font-size: 0.9rem; margin-top: 1rem; }
  .ok { color: #1a7f4b; font-size: 0.92rem; margin-top: 1rem; }
  .notice { background: ${theme.color.lemonSoft}; border-radius: ${theme.layout.radius}; padding: 1rem; font-size: 0.88rem; color: ${theme.color.onLemon}; margin-bottom: 1.5rem; }
`;

export default function Login() {
  const { signIn, signUp, ready } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState('signin');
  const [identifier, setIdentifier] = useState(''); // student ID or email
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');

  const submit = async () => {
    setErr(''); setOk(''); setBusy(true);
    const email = loginIdentifierToEmail(identifier);
    try {
      if (mode === 'signup') {
        const { error } = await signUp(email, password);
        if (error) throw error;
        setOk('Account created. Check your email to confirm, then sign in.');
        setMode('signin');
      } else {
        const { error } = await signIn(email, password);
        if (error) throw error;

        // Force a password change on first login if flagged.
        const { data: u } = await supabase.auth.getUser();
        let mustChange = false;
        if (u?.user) {
          const { data: prof } = await supabase.from('profiles').select('must_change_password').eq('id', u.user.id).maybeSingle();
          mustChange = Boolean(prof?.must_change_password);
        }
        if (mustChange) { navigate('/set-password', { replace: true }); return; }

        let target = location.state?.from?.pathname;
        if (!target) {
          let admin = false;
          let role = 'student';
          try {
            const { data: a } = await supabase.from('admins').select('email').eq('email', email).maybeSingle();
            admin = Boolean(a);
            if (u?.user) {
              const { data: p } = await supabase.from('profiles').select('role').eq('id', u.user.id).maybeSingle();
              role = p?.role || 'student';
            }
          } catch (e) { /* ignore */ }
          target = admin ? '/admin' : (role === 'staff' || role === 'admin') ? '/portal/staff' : '/portal/student';
        }
        navigate(target, { replace: true });
      }
    } catch (e) {
      setErr(e?.message || 'Could not sign in. Check your ID/email and password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Navbar />
      <Wrap>
        <img src={logo} alt="CGA Mbala Training Institute" />
        <h1>{mode === 'signup' ? 'Create your account' : 'Sign in'}</h1>
        <p className="sub">For enrolled MTI students and staff.</p>

        {!ready && (
          <div className="notice">
            Login isn’t connected yet. Add your Supabase keys to <code>.env</code> to enable accounts.
          </div>
        )}

        <label>Student ID or email</label>
        <input value={identifier} onChange={(e) => setIdentifier(e.target.value)}
          placeholder="e.g. MTI/2026/001 or you@email.com"
          onKeyDown={(e) => e.key === 'Enter' && submit()} />
        <p className="hint">Students: use the ID given by the institute. Staff: use your email.</p>
        <label>Password</label>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()} />

        <button className="primary" onClick={submit} disabled={busy || !ready || !identifier || !password}>
          {busy ? 'Please wait…' : mode === 'signup' ? 'Create account' : 'Sign in'}
        </button>

        {err && <p className="err">{err}</p>}
        {ok && <p className="ok">{ok}</p>}

        {ALLOW_SIGNUP && (
          <p className="toggle">
            {mode === 'signup' ? 'Already have an account?' : 'New here?'}{' '}
            <button onClick={() => { setMode(mode === 'signup' ? 'signin' : 'signup'); setErr(''); setOk(''); }}>
              {mode === 'signup' ? 'Sign in' : 'Create an account'}
            </button>
          </p>
        )}
        <p className="toggle">Trouble signing in? <Link to="/contact" style={{ color: theme.color.accent, fontWeight: 700 }}>Contact us</Link></p>
      </Wrap>
      <Footer />
    </>
  );
}
